/**
 * One sortable pipe row: grip | color spine | chip | cells | actions, plus
 * the expandable detail strip. Disabled containers dim the whole row.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, Copy, GripVertical, Pencil, Trash2 } from 'lucide-react';

import { ConfirmDialog, Switch } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { DayPlanContainerRow, DropGenContainerSummary } from '../../drop-generator.types';
import type { ContainerTimes } from '../../utils/slot-times';
import { containerSetupHints, summarySetupFacts } from '../../utils/setup-hints';
import { middleTruncate } from '../campaign/campaign-scope';
import { ContainerChip } from '../shared/ContainerChip';
import { ContainerDetailStrip, type DetailStripProps } from './ContainerDetailStrip';
import type { CapCuts } from '../shared/cap/cap-cuts';
import { ContainerRowCells } from './ContainerRowCells';
import type { LiveAutoSend } from '../shared/StateBadges';
import { SetupHintIcons } from './hints/SetupHintIcons';

export const BOARD_GRID =
  'grid grid-cols-[20px_4px_minmax(150px,190px)_minmax(155px,185px)_minmax(230px,1fr)_minmax(150px,170px)_100px_minmax(130px,150px)_184px] items-center gap-3 pl-3 pr-4';

interface Props {
  container: DropGenContainerSummary;
  times: ContainerTimes | undefined;
  recipientTimezone: string | null;
  preview: DayPlanContainerRow | undefined;
  cuts?: CapCuts;
  /** Phase 9/20 — its auto-send as the day shows it (State column). */
  autoSend?: LiveAutoSend;
  /** Phase 33 — the day plan's run date, for "not today". */
  runDate?: string | null;
  /** Shown under the chip when every campaign is on the board. */
  campaignLabel?: string;
  /** Everything the expanded detail strip needs. */
  detail: DetailStripProps;
  onEdit: () => void;
  onDuplicate: () => void;
  duplicating: boolean;
  onDelete: () => Promise<void>;
  deleting: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  /** 18/10/2026 — off while the board lists a subset (🌙 filter): a move would jump hidden rows. */
  dragDisabled?: boolean;
}

export const ContainerRow: FC<Props> = ({
  container,
  times,
  recipientTimezone,
  preview,
  cuts,
  autoSend,
  runDate,
  campaignLabel,
  detail,
  onEdit,
  onDuplicate,
  duplicating,
  onDelete,
  deleting,
  onToggleEnabled,
  dragDisabled = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const hints = containerSetupHints(summarySetupFacts(container));
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: container.id, disabled: dragDisabled });

  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}>
      <div
        className={cn(
          `${BOARD_GRID} border-b border-border/40 py-2.5 text-sm`,
          expanded && 'bg-muted/30',
          !container.enabled && 'opacity-50',
        )}
      >
        <button
          type="button"
          aria-label={`Drag to reorder ${container.name}`}
          disabled={dragDisabled}
          title={dragDisabled ? 'Show every container to reorder them' : undefined}
          className="cursor-grab touch-none text-muted-foreground disabled:cursor-not-allowed disabled:opacity-30"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="h-8 w-1 rounded-full" style={{ backgroundColor: container.color }} />
        <span className="flex min-w-0 flex-col gap-1">
          <ContainerChip name={container.name} color={container.color} routeTest={container.kind === 'ROUTE_TEST'} />
          {campaignLabel ? (
            <span className="truncate text-[11px] text-muted-foreground" title={campaignLabel}>
              {middleTruncate(campaignLabel, 28)}
            </span>
          ) : null}
          {container.groupWarnings?.includes('campaign_outside_group') ? (
            <span
              className="text-[11px] text-warning"
              title="Its campaign was re-linked to another tracker campaign on the SMSWiser page — it still runs; pick one of its group's campaigns in the editor"
            >
              campaign left its group
            </span>
          ) : null}
          <SetupHintIcons hints={hints} />
        </span>
        <ContainerRowCells
          container={container}
          times={times}
          recipientTimezone={recipientTimezone}
          preview={preview}
          cuts={cuts}
          autoSend={autoSend}
          runDate={runDate}
        />
        <span className="flex items-center justify-end gap-1">
          <button
            type="button"
            aria-label={`Edit ${container.name}`}
            title={`Edit ${container.name}`}
            onClick={onEdit}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          {container.kind === 'ROUTE_TEST' ? null : (
            <button
              type="button"
              aria-label={`Duplicate ${container.name}`}
              title={duplicating ? 'Duplicating…' : `Duplicate ${container.name}`}
              onClick={onDuplicate}
              disabled={duplicating}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            aria-label={`Delete ${container.name}`}
            title={`Delete ${container.name}`}
            onClick={() => setConfirmDelete(true)}
            className="rounded-md p-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          <Switch checked={container.enabled} onCheckedChange={onToggleEnabled} aria-label={`${container.name} enabled`} />
          <button
            type="button"
            aria-label="Details"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} />
          </button>
        </span>
      </div>
      {expanded ? <ContainerDetailStrip container={container} {...detail} /> : null}
      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${container.kind === 'ROUTE_TEST' ? 'route test' : 'container'}?`}
        description={
          <>
            This will permanently remove <strong className="text-foreground">{container.name}</strong> and its configuration from this plan.
          </>
        }
        confirmLabel="Delete"
        destructive
        busy={deleting}
        onConfirm={async () => {
          await onDelete();
          setConfirmDelete(false);
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
};
